"use client"

import React from "react"
import { signOut } from "next-auth/react"
import { useRouter } from "next/navigation"

export interface SessionError {
  isSessionExpired: boolean
  message: string
}

export class SessionManager {
  private static instance: SessionManager
  private onSessionExpired?: () => void

  static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager()
    }
    return SessionManager.instance
  }

  setOnSessionExpired(callback: () => void) {
    this.onSessionExpired = callback
  }

  async handleApiError(response: Response): Promise<SessionError> {
    if (response.status === 401) {
      // Session expired - trigger logout
      if (this.onSessionExpired) {
        this.onSessionExpired()
      }
      
      return {
        isSessionExpired: true,
        message: "Your session has expired. Please log in again."
      }
    }

    if (response.status === 403) {
      return {
        isSessionExpired: false,
        message: "Access denied. You may need to upgrade your subscription."
      }
    }

    return {
      isSessionExpired: false,
      message: "An error occurred. Please try again."
    }
  }

  async handleFetchError(error: Error): Promise<SessionError> {
    console.error("Fetch error:", error)
    
    // Network errors or other fetch failures
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return {
        isSessionExpired: false,
        message: "Network error. Please check your connection and try again."
      }
    }

    return {
      isSessionExpired: false,
      message: "An unexpected error occurred. Please try again."
    }
  }
}

// Hook for managing session expiration
export function useSessionManager() {
  const router = useRouter()
  const sessionManager = SessionManager.getInstance()

  const handleSessionExpired = React.useCallback(async () => {
    try {
      // Clear any cached session data
      localStorage.removeItem('nextauth.session.token')
      sessionStorage.clear()
      
      // Sign out the user
      await signOut({ 
        redirect: false,
        callbackUrl: '/auth/signin'
      })
      
      // Redirect to sign in page
      router.push('/auth/signin')
    } catch (error) {
      console.error('Error during session cleanup:', error)
      // Force redirect even if signOut fails
      router.push('/auth/signin')
    }
  }, [router])

  // Set up the session expiration handler
  React.useEffect(() => {
    sessionManager.setOnSessionExpired(handleSessionExpired)
  }, [sessionManager, handleSessionExpired])

  return {
    sessionManager,
    handleSessionExpired
  }
}
